import { useTranslations } from '@vocab/react';
import { Box, RadioGroup, RadioItem, Stack } from 'braid-design-system';
import React, { type FunctionComponent, type RefObject } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { getLinkOutApplyClicked } from 'src/app/analytics/linkOut';
import getTrackingAdapter from 'src/app/dataAnalytics';
import { LINKOUT_APPLY_CLICKED } from 'src/app/dataAnalytics/schema/constants';
import {
  useFeatureToggleData,
  FEATURE_DATA_ANALYTICS,
} from 'src/app/featureToggle';
import { actionUpdateLinkOutApplyVia } from 'src/app/state/modules/formData/linkOutApplyVia';
import { actionUpdateIsLinkOutApplyViaSeek } from 'src/app/state/modules/tealium/isLinkOutApplyViaSeek';
import { store } from 'src/app/state/store';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { ApplicationUrl } from 'src/app/views/components/ApplicationUrl';
import { JobAdReference } from 'src/app/views/components/JobAdReference';
import type AppState from 'src/types/appState';
import type {
  ApplicationUrlRef,
  JobAdReferenceRef,
} from 'src/types/formFields';
import { ApplyViaOptions } from 'src/types/linkOut';

import translations from './.vocab';

interface Props {
  applicationUrlRef: RefObject<ApplicationUrlRef>;
  jobAdReferenceRef: RefObject<JobAdReferenceRef>;
}

export const LinkOut: FunctionComponent<Props> = ({
  applicationUrlRef,
  jobAdReferenceRef,
}: Props) => {
  const { t } = useTranslations(translations);

  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);
  const { linkOutApplyVia } = useSelector(
    (state: AppState) => ({
      linkOutApplyVia: state.formData.linkOutApplyVia,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const handleOnApplyViaChange = (value: ApplyViaOptions) => {
    dispatch(actionUpdateLinkOutApplyVia(value));
    dispatch(actionUpdateIsLinkOutApplyViaSeek(value));

    if (isPageAnalyticsEnabled) {
      const eventData = {
        eventName: LINKOUT_APPLY_CLICKED,
        isLinkOutApplyViaSeek: value === ApplyViaOptions.SEEK_WEBSITE,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    } else {
      const newTealium = store.getState().tealium;
      sendLinkEvent(newTealium, getLinkOutApplyClicked(value));
    }
  };

  return (
    <Stack space="medium">
      <RadioGroup
        id="applyViaRadioButtonGroup"
        value={linkOutApplyVia}
        onChange={({ currentTarget: { value } }) =>
          handleOnApplyViaChange(value as ApplyViaOptions)
        }
        label={t('linkout-radio-group-label')}
      >
        <RadioItem
          label={t('linkout-radio-item-label-seek-website')}
          value={ApplyViaOptions.SEEK_WEBSITE}
          data={{ cy: 'linkoutApplySeek' }}
        />
        <RadioItem
          label={t('linkout-radio-item-label-external-website')}
          value={ApplyViaOptions.EXTERNAL_WEBSITE}
          data={{ cy: 'linkoutApplyExternal' }}
        />
      </RadioGroup>

      {linkOutApplyVia === ApplyViaOptions.EXTERNAL_WEBSITE && (
        <Box paddingLeft="large">
          <Stack space="small">
            <ApplicationUrl ref={applicationUrlRef} />
            <JobAdReference ref={jobAdReferenceRef} isRequired />
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
